import {
    IS_AUTHENTICATION_DONE,
    IS_AUTHENTICATION_DONE_FLAG_TTL,
} from '@thoughtspot/blink-constants';
import _ from 'lodash';
import {
    getSessionStorageItem,
    setSessionStorageWithExpiry,
} from '/@services/answer/answer.util';
import { getSession } from '/@services/stateful/stateful-service-hooks.util';
import {
    ApolloClient,
    gql,
} from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';

// Session-storage key holding the BACH session pointer of an unsaved answer
// across a single-window (in-page) OAuth round trip to the IDP.
export const UNSAVED_ANSWER_OAUTH_CONTEXT_KEY = 'unsavedAnswerOauthContext';

export interface UnsavedAnswerOauthContext {
    sessionId: string;
    genNo: number;
    acSessionId: string;
    acGenNo: number;
    // URL the context was saved from; restoring is allowed only when landing
    // back on this exact URL after the OAuth round trip.
    href: string;
}

const SESSION_POINTER_QUERY = gql`
    {
        sessionId
        genNo
        acSession {
            sessionId
            genNo
        }
    }
`;

/**
 * Persists a pointer to the unsaved answer's BACH session right before a
 * single-window OAuth redirect replaces the document with the IDP page.
 *
 * Unlike saved answers or liveboards, an unsaved answer cannot be rebuilt
 * from its URL after the full-page reload that follows the round trip, so
 * the adhoc answer page uses this pointer to re-attach to the still-alive
 * server-side session instead of creating a fresh empty one.
 */
export function saveUnsavedAnswerOauthContext(
    client: ApolloClient<object>,
    answerSessionId: string,
): void {
    if (!answerSessionId) {
        return;
    }
    const session = getSession(
        client,
        answerSessionId,
        'BachSessionId',
        SESSION_POINTER_QUERY,
        ['acSession'],
    );
    if (
        !session?.sessionId ||
        !_.isNumber(session.genNo) ||
        !session.acSession?.sessionId ||
        !_.isNumber(session.acSession?.genNo)
    ) {
        return;
    }
    const context: UnsavedAnswerOauthContext = {
        sessionId: session.sessionId,
        genNo: session.genNo,
        acSessionId: session.acSession.sessionId,
        acGenNo: session.acSession.genNo,
        href: window.location.href,
    };
    setSessionStorageWithExpiry(
        UNSAVED_ANSWER_OAUTH_CONTEXT_KEY,
        JSON.stringify(context),
        IS_AUTHENTICATION_DONE_FLAG_TTL,
    );
}

/**
 * Returns the saved session pointer only when landing back from a
 * single-window OAuth round trip (the oauth-redirection callback sets
 * IS_AUTHENTICATION_DONE) on the same URL it was saved from; null otherwise.
 */
export function readUnsavedAnswerOauthContext(): UnsavedAnswerOauthContext | null {
    const raw = getSessionStorageItem(UNSAVED_ANSWER_OAUTH_CONTEXT_KEY);
    if (!raw || getSessionStorageItem(IS_AUTHENTICATION_DONE) !== 'true') {
        return null;
    }
    try {
        const context = JSON.parse(raw) as UnsavedAnswerOauthContext;
        if (
            !context?.sessionId ||
            !_.isNumber(context.genNo) ||
            !context.acSessionId ||
            !_.isNumber(context.acGenNo) ||
            context.href !== window.location.href
        ) {
            return null;
        }
        return context;
    } catch {
        return null;
    }
}

export function clearUnsavedAnswerOauthContext(): void {
    sessionStorage.removeItem(UNSAVED_ANSWER_OAUTH_CONTEXT_KEY);
}
